import { ROUTES } from "@/constants/routes";
import { matchPath } from "react-router-dom";

/** Settings section path, e.g. `ROUTES.SETTINGS.CONNECTORS` or a connector detail path. */
export type OpenSettingsInput = string | undefined;

export type OpenSettingsState = {
  pathname?: string;
};

export type SettingsMenuItemPathRef = {
  path: string;
};

export function normalizeOpenSettingsInput(
  input?: OpenSettingsInput,
): OpenSettingsState {
  if (input === undefined) {
    return {};
  }

  const pathname = input.startsWith("/") ? input : `/${input}`;
  if (!pathname.startsWith(ROUTES.SETTINGS.ROOT)) {
    return {};
  }

  return { pathname };
}

/** Parse connector integration id from a `/settings/connectors/:id` path. */
export function parseConnectorsIntegrationIdFromPath(
  pathname: string | undefined,
): string | undefined {
  if (!pathname) return undefined;

  const match = matchPath(
    { path: ROUTES.SETTINGS.CONNECTORS_SETTINGS, end: true },
    pathname,
  );
  return match?.params.integrationId;
}

/** Whether a settings path also asks for the manage-plan modal. */
export function isUsagePlanPath(pathname: string | undefined): boolean {
  return pathname === ROUTES.SETTINGS.USAGE_PLAN;
}

export function isSettingsRootPath(pathname: string): boolean {
  return (
    pathname === ROUTES.SETTINGS.ROOT || pathname === `${ROUTES.SETTINGS.ROOT}/`
  );
}

/**
 * The Manage modal's entry value for a settings URL: the deep-link path it
 * resolves to (a connector detail path resolves to the connectors path, which
 * the shell's alias map knows). Undefined when nothing matches.
 */
export function resolveManageEntry(
  pathname: string,
  menuItems: SettingsMenuItemPathRef[],
): string | undefined {
  if (isSettingsRootPath(pathname)) {
    return menuItems[0]?.path;
  }
  return resolveSettingsMenuItemPath(pathname, menuItems);
}

/** Whether a settings URL should open the dialog (root or a permitted menu item). */
export function isPermittedSettingsDeepLink(
  pathname: string,
  menuItems: SettingsMenuItemPathRef[],
): boolean {
  if (isSettingsRootPath(pathname)) {
    return true;
  }

  return resolveSettingsMenuItemPath(pathname, menuItems) !== undefined;
}

/** Resolve which settings menu item `path` corresponds to a settings URL. */
export function resolveSettingsMenuItemPath(
  pathname: string,
  menuItems: SettingsMenuItemPathRef[],
): string | undefined {
  const sorted = [...menuItems].sort((a, b) => b.path.length - a.path.length);

  for (const item of sorted) {
    if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
      return item.path;
    }
  }

  return undefined;
}

export function resolveSettingsMenuItemFromPath(
  pathname: string,
  menuItems: SettingsMenuItemPathRef[],
): SettingsMenuItemPathRef | undefined {
  const menuItemPath = resolveSettingsMenuItemPath(pathname, menuItems);
  if (!menuItemPath) return undefined;
  return menuItems.find((item) => item.path === menuItemPath);
}
